import { z } from 'zod';
import { HEADERS } from '../constants/constants';

// ============================================================
// ID Schemas
// ============================================================

/**
 * Coerces any value to string (handles ObjectId, string, etc.)
 * Use for _id fields — the repository layer handles the ObjectId transformation.
 */
export const coercedIdSchema = z.coerce.string();

// ============================================================
// String Schemas
// ============================================================

export const requiredStringSchema = z.string().min(1, { message: 'String cannot be empty' });

// ============================================================
// Date Schemas
// ============================================================

/** Validates DD.MM.YYYY format strings (calendar day, timezone resolved via header). */
export const calendarDateField = z.string().regex(/^\d{2}\.\d{2}\.\d{4}$/, 'Expected DD.MM.YYYY format');

// ============================================================
// Boolean Schemas
// ============================================================

export const coerceBooleanFromString = z.preprocess((val) => {
	if (typeof val === 'boolean') return val;
	if (val === 'true') return true;
	if (val === 'false') return false;
	return val;
}, z.boolean());

// ============================================================
// Param Schemas
// ============================================================

export const idParamsSchema = z.object({
	id: requiredStringSchema.describe('Resource ID'),
});

/**
 * The single-project default: `/projects/:projectId/<collection>`.
 *
 * Required by construction — a path param is refused by the router before any
 * hook or schema runs, which is the whole reason the convention prefers it to a
 * query param that is one `.optional()` away from an all-projects read.
 */
export const projectIdParamsSchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
});

// ============================================================
// Query Schemas
// ============================================================

export const projectIdQuerySchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
});

// ============================================================
// Header Schemas
// ============================================================

export const tzHeadersSchema = z.object({
	[HEADERS.tz]: requiredStringSchema.describe('Timezone (e.g., Asia/Jerusalem)'),
});

export const projectIdHeadersSchema = z.object({
	[HEADERS.projectId]: requiredStringSchema.describe('Project identifier'),
});

export const projectTzHeadersSchema = z.object({
	[HEADERS.projectId]: requiredStringSchema.describe('Project identifier'),
	[HEADERS.tz]: requiredStringSchema.describe('Timezone'),
});

export const usernameHeadersSchema = z.object({
	[HEADERS.username]: requiredStringSchema.describe('Username'),
});

export const tzUsernameHeadersSchema = z.object({
	[HEADERS.tz]: requiredStringSchema.describe('Timezone'),
	[HEADERS.username]: requiredStringSchema.describe('Username'),
});

// ============================================================
// SSE Schemas
// ============================================================

export const sseQuerySchema = z.object({
	'tz': requiredStringSchema.describe('Timezone'),
	'project-id': requiredStringSchema.describe('Project ID'),
	'organization-id': requiredStringSchema.describe('Organization ID'),
	'accessToken': requiredStringSchema.describe('Access token'),
	'username': requiredStringSchema.describe('Username'),
	'is-demo': coerceBooleanFromString.optional().describe('Is demo mode'),
});

// ============================================================
// Type Exports
// ============================================================

export type ProjectIdQuery = z.infer<typeof projectIdQuerySchema>;
export type IdParams = z.infer<typeof idParamsSchema>;
export type ProjectIdParams = z.infer<typeof projectIdParamsSchema>;
